/**
 * Shared helpers for deriving a user-facing first name from a Supabase user
 * object, filtering out ugly placeholder values (e.g. "local", "admin",
 * "test") that leak through from dev/test accounts.
 *
 * Used by the Hub greeting (useHomeDashboard) and the Discover Morning Desk
 * header (MorningDeskHeader). Keep these two sites in sync.
 */

import { isStoreAccount } from '@/lib/identity/storeAccount';

const PLACEHOLDER_NAMES = new Set([
  'local',
  'user',
  'dev',
  'localhost',
  'admin',
  'test',
  'demo',
  'example',
  'sample',
  'guest',
  'anonymous',
  'anon',
  'null',
  'undefined',
]);

export function isPlaceholderName(name: string | null | undefined): boolean {
  if (!name) return true;
  return PLACEHOLDER_NAMES.has(name.toLowerCase().trim());
}

/**
 * Pick the best display name from the available sources, skipping placeholders.
 * Returns an empty string if nothing usable — callers render "there" or a
 * generic fallback.
 */
export function deriveFirstName(
  user:
    | {
        user_metadata?: { first_name?: string | null; full_name?: string | null };
        email?: string | null;
        app_metadata?: Record<string, unknown> | null;
      }
    | null
    | undefined,
): string {
  if (!user) return '';
  const meta = user.user_metadata ?? {};
  const firstName = typeof meta.first_name === 'string' ? meta.first_name.trim() : '';
  if (firstName && !isPlaceholderName(firstName) && firstName.length >= 2) return firstName;

  const fullName = typeof meta.full_name === 'string' ? meta.full_name.trim() : '';
  const firstFromFull = fullName.split(/\s+/)[0] ?? '';
  if (firstFromFull && !isPlaceholderName(firstFromFull) && firstFromFull.length >= 2) {
    return firstFromFull;
  }

  // Store accounts (Shopify installs) carry a synthetic `store_<shop>_<hex>@mora-ghost.io`
  // email — never greet by it.
  if (isStoreAccount(user)) return '';

  const emailLocal = user.email ? user.email.split('@')[0] : '';
  if (emailLocal && !isPlaceholderName(emailLocal) && emailLocal.length >= 2) return emailLocal;

  return '';
}

/**
 * Derive a first name from an account-roster profile shape (`{ fullName, email }`),
 * not a Supabase user object. Shared by the account context surfaces
 * (useCurrentContext, SharedAccountHint) so the "owner first name" derivation
 * lives in exactly one place. Returns null when nothing usable is present;
 * callers pick their own fallback.
 */
export function firstNameOf(
  p: { fullName?: string | null; email?: string | null } | null | undefined,
): string | null {
  if (!p) return null;
  if (!p.fullName && isStoreAccount({ email: p.email })) return null;
  return p.fullName?.split(' ')[0] ?? p.email?.split('@')[0] ?? null;
}

/**
 * Pick the best full display name from the available sources. Used anywhere
 * the app shows the user's name as a whole (sidebar, nav menus, headers) —
 * keep every such call site on this function so they can't drift out of sync
 * with each other again.
 */
export function getDisplayName(
  user:
    | {
        user_metadata?: { full_name?: string | null };
        email?: string | null;
        app_metadata?: Record<string, unknown> | null;
      }
    | null
    | undefined,
  fallback = 'User',
): string {
  if (!user) return fallback;
  const meta = user.user_metadata ?? {};
  const fullName = typeof meta.full_name === 'string' ? meta.full_name.trim() : '';
  if (fullName && !isPlaceholderName(fullName)) return fullName;

  if (isStoreAccount(user)) return fallback;

  const emailLocal = user.email ? user.email.split('@')[0] : '';
  if (emailLocal && !isPlaceholderName(emailLocal)) return emailLocal;

  return fallback;
}
